import axios from "axios";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { config } from "../../config";
import { useCategoryContext } from "../../context/CategoryContext";
import "./Home.css";

type CategoryOnPhoto = {
  id: number;
  name: string;
  parentId: number | null;
};

type Photo = {
  id: number;
  src: string;
  srcS?: string;
  srcL?: string;
  sizes?: string; // For responsive images
  title: string;
  location: string;
  description: string;
  fileName: string;
  sizeInfo: string;
  userId?: number;
  categories?: CategoryOnPhoto[];
};

type CurrentUser = {
  id: number;
  name: string;
  email: string;
  roles?: string[];
};

function Home() {
  const PHOTOS_PER_BATCH = 6;
  const PATH_IMAG_BASE = config.picsBaseUrl;
  const PATH_IMAG_SMALL = PATH_IMAG_BASE + '/small/';
  const PATH_IMAG_LARGE = PATH_IMAG_BASE + '/large/';
  const IMG_PREFIX_SMALL = 's_';
  const IMG_PREFIX_LARGE = 'l_';


  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const initialScrollDone = useRef(false);
  const loadMoreRef = useRef<(() => void) | null>(null);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  // Category filter (empty array = show all photos)
  const { selectedCategories, setSelectedCategories } = useCategoryContext();
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(selectedCategories);
  const selectedCategoryIdsRef = useRef<number[]>([]);

  // Initialize from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const categoryIdsParam = urlParams.get('categoryIds');
    
    if (categoryIdsParam) {
      const ids = categoryIdsParam.split(',').map(Number).filter(id => !isNaN(id));
      setSelectedCategoryIds(ids);
      setSelectedCategories(ids); // Sync with context
    }
  }, []);

  // Initialize context to match local state
  useEffect(() => {
    selectedCategoryIdsRef.current = selectedCategoryIds;
  }, [selectedCategoryIds]);

  // When component mounts, sync the context state with local state
  useEffect(() => {
    setSelectedCategories(selectedCategoryIds);
  }, []);

  // Listen for changes to context selectedCategories and update local state
  useEffect(() => {
    setSelectedCategoryIds(selectedCategories);
  }, [selectedCategories]);

  // Edit modal state
  const [editingPhoto, setEditingPhoto] = useState<Photo | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Photo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const mapImageToPhoto = (image: any): Photo => {
    const smallUrl = image.fileName ? `${PATH_IMAG_SMALL}${IMG_PREFIX_SMALL}${image.fileName}` : "";
    const largeUrl = image.fileName ? `${PATH_IMAG_LARGE}${IMG_PREFIX_LARGE}${image.fileName}` : "";
    return {
      id: image.id,
      src: largeUrl,
      srcS: smallUrl || undefined,
      srcL: largeUrl || undefined,
      sizes: "(max-width: 700px) 92vw, (max-width: 1100px) 46vw, 30vw",
      title: image.title ?? "Untitled",
      location: image.location ?? "Unknown",
      description: image.description ?? "",
      fileName: image.fileName ?? "",
      sizeInfo: image.fileSize ? `${image.fileSize} bytes` : image.fileType ?? "",
      userId: image.userId ?? undefined,
      categories: Array.isArray(image.categories)
        ? image.categories.map((c: any) => ({ id: c.id, name: c.name, parentId: c.parentId ?? null }))
        : [],
    };
  };

  const fetchCurrentUser = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setCurrentUser(null);
      return;
    }
    // Keep JWT-safe chars including dots ("header.payload.signature") —
    // stripping the dots corrupts the token and the server rejects it with 400.
    const cleanedToken = token.replace(/[^-A-Za-z0-9_.=]/g, '');
    if (cleanedToken === '' || cleanedToken.split('.').length !== 3) {
      localStorage.removeItem("token");
      setCurrentUser(null);
      setIsLoggedIn(false);
      navigate("/login");
      return;
    }
    axios.get(`${config.apiBaseUrl}/user/me`, {
      headers: { Authorization: `Bearer ${cleanedToken}` },
    })
      .then((response) => setCurrentUser(response.data))
      .catch((error) => {
        // 401 = expired/invalid signature, 400 = malformed token.
        // Both mean the stored session is unusable.
        if (error.response?.status === 401 || error.response?.status === 400) {
          localStorage.removeItem("token");
          window.dispatchEvent(new Event("auth-state-changed"));
          setCurrentUser(null);
          setIsLoggedIn(false);
          navigate("/login");
          return;
        }
        console.error("Failed to fetch current user:", error);
        setCurrentUser(null);
      });
  }, []);

  const fetchPhotos = useCallback(
    (page: number, size: number, categoryIds: number[]) => {
      if (isLoadingRef.current || !hasMoreRef.current) return;
      setIsLoading(true);

      // The gallery is public: only attach the Authorization header when a
      // token is available so anonymous visitors can browse photos too.
      const token = localStorage.getItem("token");
      const cleanedToken = token ? token.replace(/[^A-Za-z0-9_.=-]/g, '') : "";
      const headers = cleanedToken ? { Authorization: `Bearer ${cleanedToken}` } : {};
      axios
        .get(`${config.apiBaseUrl}/images`, {
          params: {
            page,
            size,
            sortBy: "uploadedAt",
            sortDirection: "desc",
            ...(categoryIds.length > 0 ? { categoryIds } : {}),
          },
          headers
        })
        .then((response) => {
          //TODO: add correct error handling for 204 No Content response
          if(response && response.status === 204) {
            setHasMore(false);
            console.error("Failed to load photos from DB:", response.statusText);
            return;
          }
          const imagePage = response.data;
          if (!imagePage) {
            setHasMore(false);
            return;
          }
          const nextPhotos = (imagePage.content || []).map(mapImageToPhoto);
          setPhotos((currentPhotos) => {
            if (page === 0) {
              const uniqueNextPhotos = Array.from(new Map(nextPhotos.map((photo: Photo) => [photo.id, photo])).values());
              return uniqueNextPhotos;
            } else {
              const uniqueNewPhotos = nextPhotos.filter((newPhoto: Photo) => !currentPhotos.some((p: Photo) => p.id === newPhoto.id));
              return [...currentPhotos, ...uniqueNewPhotos];
            }
          });
            setCurrentPage(page + 1);
          setHasMore(!imagePage.last);
        })
        .catch((error) => {
          if (error.response?.status === 401 && cleanedToken) {
            localStorage.removeItem("token");
            window.dispatchEvent(new Event("auth-state-changed"));
            navigate("/login");
            return;
          }
          console.error("Failed to load photos from DB:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    []
  );

  const loadMore = useCallback(() => {
    fetchPhotos(currentPage, PHOTOS_PER_BATCH, selectedCategoryIdsRef.current);
  }, [currentPage, fetchPhotos]);

  useLayoutEffect(() => {
    loadMoreRef.current = loadMore;
    isLoadingRef.current = isLoading;
    hasMoreRef.current = hasMore;
    selectedCategoryIdsRef.current = selectedCategoryIds;
  }, [loadMore, isLoading, hasMore, selectedCategoryIds]);

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!initialScrollDone.current && currentPage === 1) {
      window.scrollTo(0, 0);
      initialScrollDone.current = true;
    }
  }, [currentPage]);

  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    setIsLoggedIn(Boolean(savedToken));

    if (savedToken) {
      fetchCurrentUser();
    }
  }, []);

  // Reset the gallery and load the first page whenever the category selection changes.
  useEffect(() => {
    setPhotos([]);
    setSelectedPhoto(null);
    setSelectedIndex(-1);
    setCurrentPage(0);
    setHasMore(true);
    hasMoreRef.current = true;
    isLoadingRef.current = false;
    
    // This will trigger re-fetching with new category filter
    fetchPhotos(0, PHOTOS_PER_BATCH, selectedCategoryIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryIds]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoadingRef.current && hasMoreRef.current) {
            loadMoreRef.current?.();
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "200px",
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isLoading && hasMore && sentinelRef.current) {
      const rect = sentinelRef.current.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 200) {
        loadMoreRef.current?.();
      }
    }
  }, [photos, isLoading, hasMore]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    if (token) {
      localStorage.setItem("token", token);
      urlParams.delete("token");
      const newUrl = `${window.location.pathname}`;
      (window as Window).history.replaceState({}, document.title, newUrl);
      setIsLoggedIn(true);
      fetchCurrentUser();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Modals take priority over lightbox navigation
      if (editingPhoto) {
        if (event.key === "Escape") {
          event.preventDefault();
          handleCloseEditModal();
        }
        return;
      }
      if (deleteTarget) {
        if (event.key === "Escape") {
          event.preventDefault();
          handleCancelDelete();
        }
        return;
      }
      if (!selectedPhoto) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          handlePrevPhoto();
          break;
        case "ArrowRight":
          event.preventDefault();
          handleNextPhoto();
          break;
        case "Escape":
          event.preventDefault();
          handleCloseLightbox();
          break;
      }
    };

    if (selectedPhoto || editingPhoto || deleteTarget) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedPhoto, selectedIndex, photos, editingPhoto, deleteTarget]);

const handlePrevPhoto = () => {
    if (selectedIndex > 0) {
      const prevIndex = selectedIndex - 1;
      setSelectedPhoto(photos[prevIndex]);
      setSelectedIndex(prevIndex);
    }
  };

  const handleNextPhoto = () => {
    if (selectedIndex < photos.length - 1) {
      const nextIndex = selectedIndex + 1;
      setSelectedPhoto(photos[nextIndex]);
      setSelectedIndex(nextIndex);
    }
  };

  const handleCloseLightbox = () => {
    setSelectedPhoto(null);
    setSelectedIndex(-1);
  };

  // Only the owner can edit/delete a photo
  // Only the owner or an ADMIN may edit/delete a photo
  const canManagePhoto = useCallback(
    (photo: Photo) => {
      if (!isLoggedIn || !currentUser) return false;
      const isAdmin = Boolean(currentUser.roles?.some((role) => role === "ROLE_ADMIN" || role === "ADMIN"));
      return photo.userId === currentUser.id || isAdmin;
    },
    [isLoggedIn, currentUser]
  );

  // ---- Edit handlers ----
  const handleOpenEdit = (photo: Photo) => {
    setEditTitle(photo.title);
    setEditLocation(photo.location);
    setEditDescription(photo.description);
    setEditError(null);
    setEditingPhoto(photo);
  };

  const handleCloseEditModal = () => {
    if (isSavingEdit) return;
    setEditingPhoto(null);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingPhoto) return;
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      setIsSavingEdit(true);
      setEditError(null);
      await axios.put(
        `${config.apiBaseUrl}/images/${editingPhoto.id}`,
        {
          title: editTitle,
          location: editLocation,
          description: editDescription,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Update gallery + lightbox state with the new values
      const updated = {
        ...editingPhoto,
        title: editTitle,
        location: editLocation,
        description: editDescription,
      };
      setPhotos((prev) => prev.map((p) => (p.id === editingPhoto.id ? updated : p)));
      setSelectedPhoto((prev) => (prev && prev.id === editingPhoto.id ? updated : prev));
      setEditingPhoto(null);
    } catch (error: any) {
      console.error("Update failed:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      if (error.response?.status === 403) {
        setEditError("You don't have permission to edit this picture.");
        return;
      }
      setEditError(error?.response?.data?.message || "Failed to save changes. Please try again.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  // ---- Delete handlers ----
  const handleConfirmDelete = (photo: Photo) => {
    setDeleteError(null);
    setDeleteTarget(photo);
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      setIsDeleting(true);
      setDeleteError(null);
      await axios.delete(`${config.apiBaseUrl}/images/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhotos((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setSelectedPhoto(null);
      setSelectedIndex(-1);
      setDeleteTarget(null);
    } catch (error: any) {
      console.error("Delete failed:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      if (error.response?.status === 403) {
        setDeleteError("You don't have permission to delete this picture.");
        return;
      }
      setDeleteError(error?.response?.data?.message || "Failed to delete the picture. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <main className="home-page">
      <Header />
      <section className="gallery-section" id="gallery">
        {!isLoading && photos.length === 0 && (
          <div className="empty-gallery">
            <p>No photos in this category yet.</p>
          </div>
        )}
        <div className="masonry-grid">
          {photos.map((photo, index) => (
            <button
              key={photo.id}
              className="photo-card"
              onClick={() => {
                setSelectedPhoto(photo);
                setSelectedIndex(index);
              }}
              type="button"
              aria-label={`Open photo: ${photo.title}`}
            >
              <span className="photo-frame">
                <img
                  src={photo.srcS || photo.src}
                  srcSet={
                    photo.srcS && photo.srcL
                      ? `${photo.srcS} 320w, ${photo.srcL} 1200w`
                      : undefined
                  }
                  sizes={
                    photo.srcS && photo.srcL
                      ? "(max-width: 700px) 92vw, (max-width: 1100px) 46vw, 30vw"
                      : undefined
                  }
                  alt={photo.title}
                  loading="lazy"
                  onLoad={(e) => e.currentTarget.classList.add("loaded")}
                />
              </span>
            </button>
          ))}
        </div>

          <div ref={sentinelRef} id="scroll-sentinel" className="scroll-sentinel">
          {isLoading && <span>Loading more photos...</span>}
          {!isLoading && hasMore && <span>Scroll down to load more</span>}
          {!hasMore && <span>End of gallery</span>}
        </div>
      </section>

      {selectedPhoto && (
        <div className="lightbox" onClick={handleCloseLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={handleCloseLightbox}>
              ✕
            </button>

            {selectedIndex > 0 && (
              <button className="nav-btn nav-prev" onClick={handlePrevPhoto}>
                ‹
              </button>
            )}

            {selectedIndex < photos.length - 1 && (
              <button className="nav-btn nav-next" onClick={handleNextPhoto}>
                ›
              </button>
            )}
            <img
              src={selectedPhoto.srcL || selectedPhoto.src}
              alt={selectedPhoto.title}
            />
            <div className="lightbox-caption">
              <h3>{selectedPhoto.title}</h3>
              {/* <p>{selectedPhoto.location}</p>
              {selectedPhoto.description && <p className="caption-description">{selectedPhoto.description}</p>}
              <small>{selectedPhoto.fileName} • {selectedPhoto.sizeInfo}</small> */}

              {canManagePhoto(selectedPhoto) && (
                <div className="lightbox-actions">
                  <button className="btn btn-edit" onClick={() => handleOpenEdit(selectedPhoto)}>
                    Edit
                  </button>
                  <button className="btn btn-delete" onClick={() => handleConfirmDelete(selectedPhoto)}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editingPhoto && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Picture</h2>
            <p className="modal-subtitle">Update the properties of this picture.</p>

            <form
              className="modal-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveEdit();
              }}
            >
              <label>
                Title
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Photo title"
                />
              </label>

              <label>
                Location
                <input
                  value={editLocation}
                  onChange={(e) => setEditLocation(e.target.value)}
                  placeholder="Location or place"
                />
              </label>

              <label>
                Description
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Optional description"
                />
              </label>

              {editError && <div className="modal-message error">{editError}</div>}

              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={handleCloseEditModal} disabled={isSavingEdit}>
                  Cancel
                </button>
                <button type="submit" className="btn primary" disabled={isSavingEdit}>
                  {isSavingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal-content modal-content-danger" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Picture</h2>
            <p className="modal-subtitle">
              Are you sure you want to delete <strong>"{deleteTarget.title}"</strong>?
              This action cannot be undone and will permanently remove the image files.
            </p>

            {deleteError && <div className="modal-message error">{deleteError}</div>}

            <div className="modal-actions">
              <button type="button" className="btn secondary" onClick={handleCancelDelete} disabled={isDeleting}>
                Cancel
              </button>
              <button type="button" className="btn danger" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Home;

